import React, { useEffect, useRef, useCallback } from 'react';

const NeuralWeaveBackground = ({ className = '' }) => {
  const canvasRef = useRef(null);
  const nodesRef = useRef(null);
  const pointerRef = useRef({ x: 0, y: 0, active: false });
  const animationRef = useRef(null);

  // Configuration
  const CONFIG = {
    NODE_COUNT: 90,
    LINK_DISTANCE: 135,
    MOUSE_DISTANCE: 210,
    BASE_SPEED: 0.75,
    DRIFT: 0.020,
    DAMP: 0.985,
    MOUSE_PULL: 0.00125,
    MOUSE_SWIRL: 0.00075,
  };

  // Color palettes
  const PALETTE_DARK = [
    [90, 190, 255],   // cyan-blue
    [70, 120, 255],   // blue
    [140, 90, 255],   // purple
    [255, 90, 205],   // pink
    [255, 155, 90],   // orange accent
    [80, 240, 210],   // aqua
  ];

  const PALETTE_LIGHT = [
    [15, 80, 140],    // deep blue
    [40, 110, 210],   // blue
    [120, 70, 200],   // purple
    [200, 70, 150],   // magenta
    [20, 160, 140],   // teal
  ];

  const isDarkMode = useCallback(() => {
    return document.documentElement.classList.contains('dark');
  }, []);

  const pickPaletteColor = useCallback(() => {
    const pal = isDarkMode() ? PALETTE_DARK : PALETTE_LIGHT;
    const base = pal[Math.floor(Math.random() * pal.length)];
    const jitter = () => (Math.random() - 0.5) * 18;
    return [
      Math.max(0, Math.min(255, Math.round(base[0] + jitter()))),
      Math.max(0, Math.min(255, Math.round(base[1] + jitter()))),
      Math.max(0, Math.min(255, Math.round(base[2] + jitter()))),
    ];
  }, [isDarkMode]);

  const rgba = (rgb, a) => `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${a})`;

  const mixRGB = (a, b, t) => [
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
    Math.round(a[2] + (b[2] - a[2]) * t),
  ];

  const clamp01 = (x) => Math.max(0, Math.min(1, x));

  const initNodes = useCallback((width, height) => {
    return Array.from({ length: CONFIG.NODE_COUNT }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * CONFIG.BASE_SPEED,
      vy: (Math.random() - 0.5) * CONFIG.BASE_SPEED,
      r: 1.2 + Math.random() * 1.7,
      c: pickPaletteColor(),
    }));
  }, [pickPaletteColor]);

  const recolorNodes = useCallback(() => {
    if (nodesRef.current) {
      for (const n of nodesRef.current) {
        n.c = pickPaletteColor();
      }
    }
  }, [pickPaletteColor]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    
    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      
      if (!nodesRef.current) {
        nodesRef.current = initNodes(window.innerWidth, window.innerHeight);
      }
    };

    const handleMouseMove = (e) => {
      pointerRef.current.x = e.clientX;
      pointerRef.current.y = e.clientY;
      pointerRef.current.active = true;
    };

    const handleMouseLeave = () => {
      pointerRef.current.active = false;
    };

    const handleTouchStart = (e) => {
      const t = e.touches?.[0];
      if (t) {
        pointerRef.current.x = t.clientX;
        pointerRef.current.y = t.clientY;
        pointerRef.current.active = true;
      }
    };

    const handleTouchMove = (e) => {
      const t = e.touches?.[0];
      if (t) {
        pointerRef.current.x = t.clientX;
        pointerRef.current.y = t.clientY;
        pointerRef.current.active = true;
      }
    };

    const handleTouchEnd = () => {
      pointerRef.current.active = false;
    };

    const wrap = (n) => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      if (n.x < -12) n.x = w + 12;
      if (n.x > w + 12) n.x = -12;
      if (n.y < -12) n.y = h + 12;
      if (n.y > h + 12) n.y = -12;
    };

    const animate = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const nodes = nodesRef.current;
      const pointer = pointerRef.current;

      if (!nodes) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }

      const dark = isDarkMode();
      const trailAlpha = dark ? 0.10 : 0.12;
      const linkGain = dark ? 0.30 : 0.22;
      const nodeAlpha = dark ? 0.92 : 0.82;
      const bgColor = dark ? 'rgba(11, 15, 20, ' : 'rgba(246, 248, 251, ';

      // Trail fill
      ctx.fillStyle = bgColor + trailAlpha + ')';
      ctx.fillRect(0, 0, w, h);

      // Links
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;

          if (d2 < CONFIG.LINK_DISTANCE * CONFIG.LINK_DISTANCE) {
            const d = Math.sqrt(d2);
            const t = 1 - d / CONFIG.LINK_DISTANCE;
            const mix = mixRGB(a.c, b.c, 0.5);
            const alpha = clamp01(linkGain * t);

            ctx.strokeStyle = rgba(mix, alpha);
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // Nodes
      for (const n of nodes) {
        // Drift
        n.vx += (Math.random() - 0.5) * CONFIG.DRIFT;
        n.vy += (Math.random() - 0.5) * CONFIG.DRIFT;

        // Pointer influence
        if (pointer.active) {
          const dx = pointer.x - n.x;
          const dy = pointer.y - n.y;
          const d2 = dx * dx + dy * dy;

          if (d2 < CONFIG.MOUSE_DISTANCE * CONFIG.MOUSE_DISTANCE) {
            const d = Math.sqrt(d2) || 1;
            const t = 1 - d / CONFIG.MOUSE_DISTANCE;

            n.vx += dx * (CONFIG.MOUSE_PULL * t);
            n.vy += dy * (CONFIG.MOUSE_PULL * t);
            n.vx += -dy * (CONFIG.MOUSE_SWIRL * t);
            n.vy += dx * (CONFIG.MOUSE_SWIRL * t);
          }
        }

        // Integrate + damp
        n.x += n.vx;
        n.y += n.vy;
        n.vx *= CONFIG.DAMP;
        n.vy *= CONFIG.DAMP;

        wrap(n);

        // Draw node
        ctx.fillStyle = rgba(n.c, nodeAlpha);
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    // Setup
    resize();
    window.addEventListener('resize', resize, { passive: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('mouseleave', handleMouseLeave, { passive: true });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    // Start animation
    animationRef.current = requestAnimationFrame(animate);

    // Theme change observer
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'class') {
          recolorNodes();
        }
      }
    });
    observer.observe(document.documentElement, { attributes: true });

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      observer.disconnect();
    };
  }, [initNodes, isDarkMode, recolorNodes]);

  return (
    <canvas
      ref={canvasRef}
      className={`fixed inset-0 w-screen h-screen pointer-events-none ${className}`}
      style={{ zIndex: -10 }}
    />
  );
};

export default NeuralWeaveBackground;
